const PLUGIN_NAME = 'iwepnm_hotel';

import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import {useSelector} from 'react-redux';
import {Form, Label} from 'semantic-ui-react';

import {RadioButton, Select} from 'indico/react/components';
import {FinalField, FinalInput, validators, parsers} from 'indico/react/forms';
import {Param, Translate} from 'indico/react/i18n';

import {getPriceFormatter} from './selectors';
import {getFieldValue, getManagement, getPaid} from './submission_selectors';

import ChoiceLabel from './ChoiceLabel';
import {Choices, choiceShape, choiceFieldsSettingsFormDecorator} from './ChoicesSetup';

import {registerPluginObject} from 'indico/utils/plugins';

import './regform.module.scss';

function IwepnmHotelInputComponent({
  id,
  existingValue,
  value,
  onChange,
  disabled,
  isPurged,
  choices,
  conferencefee,
  accompanyingfee,
  childprice,
}) {

  const paid = useSelector(getPaid);
  const management = useSelector(getManagement);
  const formatPrice = useSelector(getPriceFormatter);
  const selectedChoice = choices.find(c => c.id === value.choice) || {};
  const selectedFallback = choices.find(c => c.id === value.fallback) || {};
  const plan = choices.find(c => c.id === value.plan) || {};
  const assigned = choices.find(c => c.id === value.assigned) || {};

  // price of the category that will be billed: the assigned one if set, else the first choice;
  // undefined until a category has been selected
  const hotelPrice = (typeof assigned.price === "number" ? assigned.price : selectedChoice.price)
  const hasHotelPrice = typeof hotelPrice === "number";

  const adults = value.adults || 0;
  const children = value.children || 0;
  const babies = value.babies || 0;
  const adjust = value.adjust || 100;

  // Number inputs: an emptied input must never leave `null` in the form state
  // (the server rejects null with "Field may not be null." while the input
  // shows 0 again); parse to a non-negative integer, empty -> fallback.
  const parseCount = (raw, fallback) => {
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n >= 0 ? n : fallback;
  };

  const makeHandleChangeChoice = choice => () => {
    const newValue = {...value, choice: choice.id};
    onChange(newValue);
  };

  const handleChangeFallback = evt => {
    if (!evt.target.value) {
      onChange({...value, fallback: null});
      return;
    }
    onChange({...value, fallback: evt.target.value});
  };

  const handleChangePlan = evt => {
    if (!evt.target.value) {
      onChange({...value, plan: null});
      return;
    }
    onChange({...value, plan: evt.target.value});
  };

  const handleChangeAssigned = evt => {
    if (!evt.target.value) {
      onChange({...value, assigned: null});
      return;
    }
    onChange({...value, assigned: evt.target.value});
  };

  const handleChangeAcAdults = evt => {
    onChange({...value, adults: parseCount(evt.target.value, 0)});
  };

  const handleChangeAcChildren = evt => {
    onChange({...value, children: parseCount(evt.target.value, 0)});
  };

  const handleChangeAcBabies = evt => {
    onChange({...value, babies: parseCount(evt.target.value, 0)});
  };

  const handleChangeAdjustTotal = evt => {
    onChange({...value, adjust: parseCount(evt.target.value, 100)});
  };

  const isPaidChoice = choice => choice.price > 0 && paid;
  const isPaidChoiceLocked = choice => !management && isPaidChoice(choice);

  const options = choices.filter(choice => management || !choice.hidden).map(choice => ({
    value: choice.id,
    label: (
      <div styleName="dropdown-text" key={choice.id}>
        <div styleName="caption" data-label>
          <ChoiceLabel choice={choice} management={management} />
        </div>
      </div>
    ),
    disabled:
      !choice.isEnabled ||
      isPaidChoiceLocked(choice)
  }));

  return (
    <div styleName="iwepnmhotel-field">
      <legend>Please select your choice of hotel category:</legend>
      <table styleName="choice-table" role="presentation" id={id}>
        <tbody>
          {choices
            .filter(c => management || !c.hidden)
            .map((c, index) => {
              return (
                <tr key={c.id} styleName="row">
                  <td>
                    <RadioButton
                      id={id ? `${id}-${index}` : ''}
                      name={id}
                      key={c.id}
                      value={c.id}
                      checked={!isPurged && c.id === value.choice}
                      disabled={
                        !c.isEnabled ||
                        disabled ||
                        isPaidChoiceLocked(c)
                      }
                      label={
                        <ChoiceLabel choice={c} management={management} paid={isPaidChoice(c)} />
                      }
                      onChange={makeHandleChangeChoice(c)}
                    />
                  </td>
                  <td>
                    {c.isEnabled && !!c.price && (
                      <Label pointing="left">
                        <Translate>
                          <Param name="price" value={formatPrice(c.price)} />
                        </Translate>
                      </Label>
                    )}
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>

      <legend>In case we are unable to fulfill your first choice, please select a second choice hotel category:</legend>
        <Form.Group styleName="single-choice-dropdown">
          <Form.Field>
            <Select
              id={`${id}-fallback`}
              onChange={handleChangeFallback}
              options={options}
              value={(!isPurged && selectedFallback.id) || ''}
            />
          </Form.Field>
        </Form.Group>

      <legend>Accompanying adults and children from 13 years:</legend>
      <div styleName="number-field">
      <input
        id={`${id}-adults`}
        type="number"
        value={adults !== null ? adults : ''}
        disabled={disabled}
        onChange={handleChangeAcAdults}
      />
      {(
        <Label pointing="left" styleName="price-tag">
          {hasHotelPrice ? formatPrice(hotelPrice + accompanyingfee) : "select a hotel category first"} per person
        </Label>
      )}
      </div>

      <legend>Accompanying children from 2 to 12 years:</legend>
      <div styleName="number-field">
      <input
        id={`${id}-children`}
        type="number"
        value={children !== null ? children : ''}
        disabled={disabled}
        onChange={handleChangeAcChildren}
      />
      {(
        <Label pointing="left" styleName="price-tag">
          {formatPrice(childprice)} per child
        </Label>
      )}
      </div>

      <legend>Accompanying children younger than 2 years:</legend>
      <div styleName="number-field">
      <input
        id={`${id}-babies`}
        type="number"
        value={babies !== null ? babies : ''}
        disabled={disabled}
        onChange={handleChangeAcBabies}
      />
      {(
        <Label pointing="left" styleName="price-tag">
          Free
        </Label>
      )}
      </div>


      {management && (
    <div styleName="management">
      <legend>Planned hotel category:</legend>
        <Form.Group styleName="single-choice-dropdown">
          <Form.Field>
            <Select
              id={`${id}-plan`}
              onChange={handleChangePlan}
              options={options}
              value={(!isPurged && plan.id) || ''}
            />
          </Form.Field>
        </Form.Group>
      <legend>Assigned hotel category:</legend>
        <Form.Group styleName="single-choice-dropdown">
          <Form.Field>
            <Select
              id={`${id}-assigned`}
              onChange={handleChangeAssigned}
              options={options}
              value={(!isPurged && assigned.id) || ''}
            />
          </Form.Field>
        </Form.Group>

      <legend>Adjust total price (in percent):</legend>
          <Form.Group styleName="number-field">
          <Form.Field>
          <input
            id={`${id}-adjust`}
            type="number"
            value={adjust !== null ? adjust : ''}
            disabled={disabled}
            onChange={handleChangeAdjustTotal}
          />
          </Form.Field>
          </Form.Group>
        </div>
      )}

        <div styleName="conferencefee">
          Conference fee:{' '}
          <Param name="price" value={formatPrice(conferencefee)} />
        </div>


        <div styleName="total">
                Total price:{' '}
                <Param name="price" value={hasHotelPrice ? formatPrice(Math.round(adjust*(conferencefee + hotelPrice + adults*(hotelPrice + accompanyingfee) + children*childprice))/100.0) : "select a hotel category first"} />
        </div>
    </div>
  );
}

IwepnmHotelInputComponent.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
  isPurged: PropTypes.bool.isRequired,
  choices: PropTypes.arrayOf(PropTypes.shape(choiceShape)).isRequired,
  conferencefee: PropTypes.number.isRequired,
  accompanyingfee: PropTypes.number.isRequired,
  childprice: PropTypes.number.isRequired,
  existingValue: PropTypes.shape({choice: PropTypes.string, fallback: PropTypes.string}).isRequired,
};

export default function IwepnmHotelInput({
  fieldId,
  htmlId,
  htmlName,
  disabled,
  isRequired,
  isPurged,
  choices,
  conferencefee,
  accompanyingfee,
  childprice,
}) {
  const existingValue = useSelector(state => getFieldValue(state, fieldId)) || {choice: null, fallback: null};
  return (
    <FinalField
      id={htmlId}
      name={htmlName}
      existingValue={existingValue}
      component={IwepnmHotelInputComponent}
      required={isRequired}
      disabled={disabled}
      isPurged={isPurged}
      choices={choices}
      conferencefee={conferencefee}
      accompanyingfee={accompanyingfee}
      childprice={childprice}
      validate={value => {
        // The field is always mandatory (alwaysRequired only affects the display and
        // the form's own "required" flag is usually off), so validate unconditionally.
        if (!value.choice) {
          return Translate.string('Please select a hotel category');
        } else if (!value.fallback) {
          return Translate.string('Please select a second choice hotel category');
        } else if (value.fallback === value.choice) {
          return Translate.string('The second choice must differ from the first choice');
        }
      }}
      isEqual={_.isEqual}
    />
  );
}

IwepnmHotelInput.propTypes = {
  fieldId: PropTypes.number.isRequired,
  htmlId: PropTypes.string.isRequired,
  htmlName: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  isRequired: PropTypes.bool,
  isPurged: PropTypes.bool.isRequired,
  choices: PropTypes.arrayOf(PropTypes.shape(choiceShape)).isRequired,
  conferencefee: PropTypes.number.isRequired,
  accompanyingfee: PropTypes.number.isRequired,
  childprice: PropTypes.number.isRequired,
};

IwepnmHotelInput.defaultProps = {
  disabled: false,
  isRequired: false,
};

export const iwepnmHotelSettingsInitialData = staticData => ({
  choices: [
    {
      id: `new:S`,
      caption: 'Category S',
      isEnabled: true,
      price: 950,
      accompanyable: false,
      hidden: true
    },{
      id: `new:A`,
      caption: 'Category A',
      isEnabled: true,
      price: 490,
      accompanyable: true,
      hidden: true
    },{
      id: `new:B`,
      caption: 'Category B',
      isEnabled: true,
      price: 340,
      accompanyable: true,
      hidden: true
    },{
      id: `new:C`,
      caption: 'Category C',
      isEnabled: true,
      price: 285,
      accompanyable: true,
      hidden: true
    },{
      id: `new:X`,
      caption: 'Category X',
      isEnabled: true,
      price: 0,
      accompanyable: false,
      hidden: true
    },
  ],
  conferencefee: 720,
  accompanyingfee: 250,
  childprice: 120
});

export function IwepnmHotelSettings() {
  const validate = validators.chain(validators.required, validators.number());
  return (
    <>
      <FinalField
        name="choices"
        label={Translate.string('Choices')}
        component={Choices}
        isEqual={_.isEqual}
        required
        validate={val => {
          if (!val.some(c => c.isEnabled)) {
            return Translate.string('You need to have at least one enabled option.');
          }
        }}
      />
      <FinalInput
      id="conferencefee"
      name="conferencefee"
      type="number"
      required
      label="Conference fee"
      parse={parsers.number}
      validate={validate}
        />
      <FinalInput
      id="accompanyingfee"
      name="accompanyingfee"
      type="number"
      required
      label="Additional fee for accompanying person"
      parse={parsers.number}
      validate={validate}
        />
      <FinalInput
      id="childprice"
      name="childprice"
      type="number"
      required
      label="Price for children"
      parse={parsers.number}
      validate={validate}
        />
    </>
  );
}

export const customFields = {
    name: "ext__iwepnmhotel",
    title: "IWEPNM Hotel",
    inputComponent: IwepnmHotelInput,
    hasPrice: false,
    icon: 'home',
    settingsComponent: IwepnmHotelSettings,
    settingsModalSize: 'small',
    settingsFormDecorators: [choiceFieldsSettingsFormDecorator],
    settingsFormInitialData: iwepnmHotelSettingsInitialData,
 //   settingsFormValidator: iwepnmHotelSettingsFormValidator,
    noRequired: true,
    alwaysRequired: true,
    icon: 'home',
    renderAsFieldset: true,
}

registerPluginObject(PLUGIN_NAME, 'regformCustomFields', customFields);





